import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { Broker } from "../../components/BrokerMultiSelect";
import { useConfirm } from "../../components/Dialog";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import { LeadStatus, OnboardingRow } from "../../types/onboarding";
import ActionButtons from "./components/ActionButtons";
import ExpandedRow from "./components/ExpandedRow";
import FiltersBar from "./components/FiltersBar";
import Pagination from "./components/Pagination";
import StatusBadge from "./components/StatusBadge";
import TraitsBadges from "./components/TraitsBadges";

type ApiResponse = {
  rows: OnboardingRow[];
  total: number;
  limit: number;
  offset: number;
};

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  const datePart = d.substring(0, 10);
  const [y, m, day] = datePart.split("-");
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

export type LeadsViewProps = {
  endpoint: string;
  title: string;
  subtitle: string;
  availableStatuses: readonly LeadStatus[];
  defaultStatuses: readonly LeadStatus[];
};

export default function LeadsView({
  endpoint,
  title,
  subtitle,
  availableStatuses,
  defaultStatuses,
}: LeadsViewProps) {
  const { token } = useAuth();
  const { markets } = useMarket();
  const confirm = useConfirm();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<LeadStatus[]>([
    ...defaultStatuses,
  ]);
  const [carrier, setCarrier] = useState("");
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [brokersLoading, setBrokersLoading] = useState(true);
  const [selectedBrokerIds, setSelectedBrokerIds] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [rows, setRows] = useState<OnboardingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<null | "send-to-insurer">(null);
  const [bulkMessage, setBulkMessage] = useState<
    | { kind: "success" | "error" | "info"; text: string }
    | null
  >(null);

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setBrokersLoading(true);
    request<{ items: Broker[] }>("/api/admin/operations/brokers/options", { token })
      .then((data) => {
        if (cancelled) return;
        setBrokers(data.items);
        setSelectedBrokerIds(data.items.map((b) => b.id));
      })
      .catch(() => {
        if (cancelled) return;
        setBrokers([]);
        setSelectedBrokerIds([]);
      })
      .finally(() => {
        if (!cancelled) setBrokersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    selectedStatuses.forEach((s) => params.append("status[]", s));
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (carrier) params.set("carrier", carrier);
    if (
      brokers.length > 0
      && selectedBrokerIds.length > 0
      && selectedBrokerIds.length < brokers.length
    ) {
      selectedBrokerIds.forEach((id) => params.append("broker[]", String(id)));
    }
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));

    request<ApiResponse>(`${endpoint}?${params.toString()}`, { token, markets })
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows);
        setTotal(data.total);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof HttpError) {
          setError(`HTTP ${err.status}`);
        } else {
          setError("No se pudo cargar el listado");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    endpoint,
    token,
    markets,
    selectedStatuses,
    debouncedSearch,
    carrier,
    brokers,
    selectedBrokerIds,
    page,
    pageSize,
  ]);

  const carriers = useMemo(
    () => Array.from(new Set(rows.map((r) => r.carrier).filter(Boolean))).sort(),
    [rows],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleCarrier = (value: string) => {
    setCarrier(value);
    setPage(0);
  };

  const handlePageSize = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const toggleStatus = (s: LeadStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  };

  const toggleExpand = (leadId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const SEND_TO_INSURER_ELIGIBLE_STATUSES: ReadonlySet<string> = useMemo(
    () => new Set(["ready_to_send", "ops_review"]),
    [],
  );

  const toggleSelect = (leadUlid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadUlid)) next.delete(leadUlid);
      else next.add(leadUlid);
      return next;
    });
  };

  const togglePageSelect = () => {
    const visibleUlids = rows.map((r) => r.leadUlid);
    setSelected((prev) => {
      const allVisibleSelected = visibleUlids.every((u) => prev.has(u));
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleUlids.forEach((u) => next.delete(u));
      } else {
        visibleUlids.forEach((u) => next.add(u));
      }
      return next;
    });
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.leadUlid)),
    [rows, selected],
  );
  const eligibleForSendToInsurer = useMemo(
    () =>
      selectedRows.filter((r) =>
        SEND_TO_INSURER_ELIGIBLE_STATUSES.has(r.status),
      ),
    [selectedRows, SEND_TO_INSURER_ELIGIBLE_STATUSES],
  );

  const runBulkSendToInsurer = async () => {
    if (bulkBusy) return;
    if (eligibleForSendToInsurer.length === 0) return;
    const ok = await confirm({
      title: "Enviar a la aseguradora (bulk)",
      message:
        `Se enviarán a la aseguradora los documentos de ${eligibleForSendToInsurer.length}`
        + ` lead(s) elegible(s) y cada uno pasará a Insurer review.`
        + (selectedRows.length > eligibleForSendToInsurer.length
          ? ` Los ${selectedRows.length - eligibleForSendToInsurer.length}`
            + ` lead(s) restantes seleccionados no están en ready_to_send`
            + ` ni ops_review y se omitirán.`
          : "")
        + " ¿Continuar?",
      confirmLabel: `Enviar (${eligibleForSendToInsurer.length})`,
      tone: "warning",
    });
    if (!ok) return;

    setBulkBusy("send-to-insurer");
    setBulkMessage(null);
    let failures = 0;
    for (const row of eligibleForSendToInsurer) {
      try {
        await request(
          `/api/admin/operations/leads/${row.leadUlid}/send-to-insurer`,
          { method: "POST", token, body: {} },
        );
      } catch {
        failures++;
      }
    }
    setBulkBusy(null);
    setSelected(new Set());
    // Refresh by toggling pageSize trigger — simplest: tweak page to same value
    // is a no-op; instead bump a refresh seed implicitly via re-set page 0.
    setPage((p) => p); // keep page
    setRows((prev) =>
      prev.map((r) =>
        eligibleForSendToInsurer.some((e) => e.leadUlid === r.leadUlid)
          ? { ...r, status: "insurer_review" as LeadStatus }
          : r,
      ),
    );
    if (failures > 0) {
      setBulkMessage({
        kind: "error",
        text: `${failures} lead(s) fallaron al enviar`,
      });
    } else {
      setBulkMessage({
        kind: "success",
        text: `${eligibleForSendToInsurer.length} lead(s) enviados a la aseguradora`,
      });
    }
  };

  const handleStatusChange = (leadId: number, newStatus: LeadStatus) => {
    const stillVisible = (availableStatuses as readonly string[]).includes(
      newStatus,
    );
    if (stillVisible) {
      setRows((prev) =>
        prev.map((r) =>
          r.leadId === leadId ? { ...r, status: newStatus } : r,
        ),
      );
    } else {
      setRows((prev) => prev.filter((r) => r.leadId !== leadId));
      setTotal((t) => Math.max(0, t - 1));
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  return (
    <div style={{ padding: "1.5rem 2rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
          {title}
        </h2>
        <p
          style={{
            margin: "0.25rem 0 0",
            color: "#73879C",
            fontSize: ".85rem",
          }}
        >
          {subtitle}
        </p>
      </div>

      <FiltersBar
        search={search}
        onSearch={handleSearch}
        availableStatuses={availableStatuses}
        selectedStatuses={selectedStatuses}
        onToggleStatus={toggleStatus}
        carrier={carrier}
        onCarrierChange={handleCarrier}
        carriers={carriers as string[]}
        brokers={brokers}
        selectedBrokerIds={selectedBrokerIds}
        onSelectedBrokerIdsChange={(next) => {
          setSelectedBrokerIds(next);
          setPage(0);
        }}
        brokersLoading={brokersLoading}
        total={total}
        visible={rows.length}
      />

      {selected.size > 0 && (
        <div
          style={{
            background: "#fff8e1",
            border: "1px solid #ffe69c",
            color: "#856404",
            borderRadius: "0.5rem",
            padding: "0.55rem 0.85rem",
            marginBottom: "0.65rem",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            flexWrap: "wrap",
            fontSize: ".82rem",
          }}
        >
          <strong>{selected.size}</strong> lead(s) seleccionados
          <button
            type="button"
            disabled={!!bulkBusy || eligibleForSendToInsurer.length === 0}
            onClick={runBulkSendToInsurer}
            title={
              eligibleForSendToInsurer.length === 0
                ? "Ninguno de los seleccionados está en ready_to_send u ops_review"
                : `${eligibleForSendToInsurer.length} elegible(s) (ready_to_send / ops_review)`
            }
            style={{
              padding: "0.3rem 0.7rem",
              borderRadius: "0.35rem",
              background: "#856404",
              color: "white",
              border: "1px solid #856404",
              fontSize: ".75rem",
              fontWeight: 600,
              cursor:
                bulkBusy || eligibleForSendToInsurer.length === 0
                  ? "not-allowed"
                  : "pointer",
              opacity:
                bulkBusy || eligibleForSendToInsurer.length === 0 ? 0.6 : 1,
            }}
          >
            {bulkBusy === "send-to-insurer"
              ? "Enviando…"
              : `Enviar a la aseguradora (${eligibleForSendToInsurer.length})`}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            style={{
              padding: "0.3rem 0.7rem",
              borderRadius: "0.35rem",
              background: "white",
              color: "#73879C",
              border: "1px solid #dee2e6",
              fontSize: ".75rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Deseleccionar
          </button>
        </div>
      )}

      {bulkMessage && (
        <div
          onClick={() => setBulkMessage(null)}
          style={{
            background:
              bulkMessage.kind === "success"
                ? "#d4edda"
                : bulkMessage.kind === "info"
                  ? "#e7f1ff"
                  : "#fde2e2",
            color:
              bulkMessage.kind === "success"
                ? "#155724"
                : bulkMessage.kind === "info"
                  ? "#0a58ca"
                  : "#b32424",
            padding: "0.55rem 0.85rem",
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
            cursor: "pointer",
          }}
        >
          {bulkMessage.text}
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.75rem 1rem",
            borderRadius: "0.5rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: ".85rem",
            }}
          >
            <thead>
              <tr style={{ background: "#f8f9fb" }}>
                <Th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todo"
                    checked={
                      rows.length > 0
                      && rows.every((r) => selected.has(r.leadUlid))
                    }
                    onChange={togglePageSelect}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Th>
                <Th style={{ width: 32 }}></Th>
                <Th>Lead ID</Th>
                <Th>Broker</Th>
                <Th>Customer</Th>
                <Th>Email</Th>
                <Th>DNI</Th>
                <Th title="Pólizas del mismo cliente">Pólizas</Th>
                <Th>Nº póliza</Th>
                <Th>Estado</Th>
                <Th>Traits</Th>
                <Th>Compra</Th>
                <Th>Efecto</Th>
                <Th style={{ textAlign: "right" }}>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.leadId}
                  row={row}
                  expanded={expanded.has(row.leadId)}
                  onToggle={() => toggleExpand(row.leadId)}
                  onStatusChange={(newStatus) =>
                    handleStatusChange(row.leadId, newStatus)
                  }
                  selected={selected.has(row.leadUlid)}
                  onToggleSelect={() => toggleSelect(row.leadUlid)}
                />
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={14}
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "#73879C",
                    }}
                  >
                    No hay leads que coincidan con los filtros.
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={14}
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "#73879C",
                    }}
                  >
                    Cargando…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={handlePageSize}
        disabled={loading}
      />
    </div>
  );
}

function Th({
  children,
  style,
  title,
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <th
      title={title}
      style={{
        textAlign: "left",
        padding: "0.65rem 0.75rem",
        fontSize: ".7rem",
        textTransform: "uppercase",
        color: "#73879C",
        fontWeight: 600,
        letterSpacing: "0.5px",
        borderBottom: "1px solid #e9ecef",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Row({
  row,
  expanded,
  onToggle,
  onStatusChange,
  selected,
  onToggleSelect,
}: {
  row: OnboardingRow;
  expanded: boolean;
  onToggle: () => void;
  onStatusChange: (newStatus: LeadStatus) => void;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: "pointer",
          background: selected ? "#fff8e1" : expanded ? "#f0f7ff" : "white",
          borderBottom: "1px solid #f1f3f5",
        }}
      >
        <Td>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            aria-label="Seleccionar lead"
          />
        </Td>
        <Td>
          <span
            style={{
              display: "inline-block",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
              color: "#73879C",
              fontSize: ".75rem",
            }}
          >
            ▶
          </span>
        </Td>
        <Td mono>
          <Link
            to={`/leads/${row.leadUlid}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "#0069d9",
              textDecoration: "none",
              fontWeight: 600,
            }}
            title="Abrir ficha de lead"
          >
            {row.leadId}
          </Link>
        </Td>
        <Td>{row.brokerName ?? "—"}</Td>
        <Td>{row.customer}</Td>
        <Td mono>{row.email}</Td>
        <Td mono>{row.dni ?? "—"}</Td>
        <Td>
          <span
            style={{
              display: "inline-block",
              padding: "0.1rem 0.5rem",
              borderRadius: "999px",
              background: row.policyCount > 1 ? "#fff3cd" : "#e9ecef",
              color: row.policyCount > 1 ? "#856404" : "#495057",
              fontSize: ".75rem",
              fontWeight: 600,
            }}
          >
            {row.policyCount}
          </span>
        </Td>
        <Td mono>{row.policyNumber ?? "—"}</Td>
        <Td>
          <StatusBadge status={row.status} />
          {row.pendingCancellationRequestId && (
            <Link
              to="/operations/cancellation-requests"
              title={`Solicitud de cancelación en curso (${row.pendingCancellationRequestStatus ?? ""})`}
              style={{
                display: "inline-block",
                marginLeft: "0.35rem",
                padding: "0.05rem 0.4rem",
                borderRadius: "0.3rem",
                background: "#fff3cd",
                color: "#856404",
                fontSize: ".68rem",
                fontWeight: 700,
                textDecoration: "none",
                border: "1px solid #ffe69c",
              }}
            >
              Cancelación
            </Link>
          )}
        </Td>
        <Td>
          <TraitsBadges traits={row.traits} />
        </Td>
        <Td>{fmtDate(row.purchaseDate)}</Td>
        <Td>{fmtDate(row.effectDate)}</Td>
        <Td style={{ textAlign: "right" }}>
          <ActionButtons row={row} onStatusChange={onStatusChange} />
        </Td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={14} style={{ padding: 0 }}>
            <ExpandedRow row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function Td({
  children,
  mono,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.65rem 0.75rem",
        verticalAlign: "middle",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        fontSize: mono ? ".8rem" : undefined,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </td>
  );
}
